import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Warning, GpsSlash, ArrowsClockwise, Clock, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LocationCell, TimeRecordDetailDialog } from "@/components/TimeRecordDetail";
import { CloseShiftDialog } from "@/components/CloseShiftDialog";
import { EditShiftDialog } from "@/components/EditShiftDialog";
import { formatClockTime, elapsedSince, formatSeconds, COMPANY_TZ_LABEL } from "@/lib/time";

const POLL_MS = 30000;

export default function LiveShiftBoard() {
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tick, setTick] = useState(0);
    const [selected, setSelected] = useState(null);
    const [closingEntry, setClosingEntry] = useState(null);
    const [editingEntry, setEditingEntry] = useState(null);
    const [lastSync, setLastSync] = useState(null);

    const load = useCallback(async (silent) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await api.get("/time/live");
            setBoard(data);
            setLastSync(new Date());
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    // Poll the server periodically; tick locally every second so the elapsed
    // column keeps counting without a manual refresh.
    useEffect(() => {
        const poll = setInterval(() => load(true), POLL_MS);
        const ticker = setInterval(() => setTick((t) => t + 1), 1000);
        return () => { clearInterval(poll); clearInterval(ticker); };
    }, [load]);

    const entries = Array.isArray(board?.entries) ? board.entries : [];
    const threshold = board?.long_open_threshold_hours ?? 14;

    return (
        <div className="space-y-6" data-testid="live-shift-board-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        Admin · Attendance
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Live Shift Board
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">
                        Everyone currently clocked in. Updates automatically · times in {COMPANY_TZ_LABEL}
                        {lastSync ? ` · synced ${lastSync.toLocaleTimeString()}` : ""}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => load(false)}
                    className="h-10 border-[#003366] text-[#003366] hover:bg-slate-50"
                    data-testid="live-refresh-button"
                >
                    <ArrowsClockwise size={16} className="mr-1.5" /> Refresh now
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border-[#E2E8F0] bg-[#003366] text-white" data-testid="live-total-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/70">
                            <Users size={16} /> Currently Clocked In
                        </div>
                        <div className="text-4xl font-bold mt-2" style={{ fontFamily: "Outfit" }} data-testid="live-total-active">
                            {board?.total_active ?? 0}
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#E2E8F0]" data-testid="live-review-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                            <Warning size={16} /> Open over {threshold}h
                        </div>
                        <div className={`text-4xl font-bold mt-2 ${(board?.needs_review || 0) > 0 ? "text-[#E6002C]" : "text-[#003366]"}`}
                            style={{ fontFamily: "Outfit" }} data-testid="live-needs-review">
                            {board?.needs_review ?? 0}
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#E2E8F0]" data-testid="live-nogps-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                            <GpsSlash size={16} /> No Clock In GPS
                        </div>
                        <div className="text-4xl font-bold mt-2 text-[#003366]" style={{ fontFamily: "Outfit" }}
                            data-testid="live-without-gps">
                            {board?.without_gps ?? 0}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {(board?.by_site?.length || 0) > 0 && (
                <Card className="border-[#E2E8F0]" data-testid="live-by-site-card">
                    <CardContent className="p-5">
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Active by site</div>
                        <div className="flex flex-wrap gap-2">
                            {board.by_site.map((s) => (
                                <div key={s.site_name}
                                    className="rounded-full border border-[#E2E8F0] bg-slate-50 px-3.5 py-1.5 text-sm"
                                    data-testid={`live-site-count-${s.site_name}`}>
                                    <span className="font-semibold text-[#003366]">{s.site_name}</span>
                                    <span className="text-slate-500">: {s.count}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card className="border-[#E2E8F0]" data-testid="live-shifts-card">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-10 text-center text-slate-500">Loading live shifts…</div>
                    ) : entries.length === 0 ? (
                        <div className="p-10 sm:p-16 text-center" data-testid="live-shifts-empty">
                            <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <Clock size={26} className="text-slate-400" />
                            </div>
                            <h3 className="font-semibold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                                No employees are currently clocked in.
                            </h3>
                            <p className="text-sm text-slate-500 mt-1.5">
                                Active shifts appear here the moment an employee clocks in.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>Employee</TableHead>
                                        <TableHead>Site</TableHead>
                                        <TableHead>Clock In</TableHead>
                                        <TableHead>Time on Shift</TableHead>
                                        <TableHead>Location</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {entries.map((e) => (
                                        <TableRow
                                            key={e.id}
                                            onClick={() => setSelected(e)}
                                            className={`cursor-pointer ${e.needs_review ? "bg-amber-50/70 hover:bg-amber-100/70" : ""}`}
                                            data-testid={`live-shift-row-${e.id}`}
                                        >
                                            <TableCell className="font-semibold text-[#003366] text-sm">
                                                {e.technician_name}
                                            </TableCell>
                                            <TableCell className="text-sm">{e.site_name}</TableCell>
                                            <TableCell className="text-sm font-mono whitespace-nowrap">
                                                {formatClockTime(e.clock_in_local)}
                                            </TableCell>
                                            <TableCell className="text-sm font-mono whitespace-nowrap" key={tick}>
                                                {elapsedSince(e.clock_in_at)}
                                            </TableCell>
                                            <TableCell><LocationCell entry={e} /></TableCell>
                                            <TableCell>
                                                {e.needs_review ? (
                                                    <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] uppercase tracking-wider whitespace-nowrap"
                                                        data-testid={`live-needs-review-badge-${e.id}`}>
                                                        <Warning size={11} className="mr-1" weight="fill" />
                                                        Open {formatSeconds(e.open_seconds)} — Needs Review
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-[#0066D9] text-white text-[10px] uppercase tracking-wider">
                                                        Working
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-8 border-[#0066D9] text-[#0066D9] hover:bg-[#0066D9]/5"
                                                    onClick={(ev) => { ev.stopPropagation(); setClosingEntry(e); }}
                                                    data-testid={`live-close-shift-${e.id}`}
                                                >
                                                    <LockKey size={13} className="mr-1" /> Close
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <CloseShiftDialog
                entry={closingEntry}
                open={!!closingEntry}
                onOpenChange={(o) => !o && setClosingEntry(null)}
                onClosed={() => load(false)}
            />

            <EditShiftDialog
                entry={editingEntry}
                open={!!editingEntry}
                onOpenChange={(o) => !o && setEditingEntry(null)}
                onSaved={() => load(false)}
            />

            <TimeRecordDetailDialog
                entry={selected}
                open={!!selected}
                onOpenChange={(o) => !o && setSelected(null)}
                onEdit={(e) => { setSelected(null); setEditingEntry(e); }}
                onCloseShift={(e) => { setSelected(null); setClosingEntry(e); }}
            />
        </div>
    );
}
